import {type TrendsCategory} from '../components/TrendsCategoryChips'
import {type TrendsScope} from '../components/TrendsScopeTabs'

/**
 * Types + mock data for the Trends feature.
 *
 * Mirrors the data model in `.agents/repos/root/trends/trends-how-it-works.md`.
 * The mock data was copied verbatim from `trends-mock-data.json` (uploaded
 * with the original handover) and will be replaced by `GET /trends` when the
 * backend lands. Swap `useMockTrends` for the live query at that point.
 */

export type TrendActivityStatus =
  | 'news_media_trend'
  | 'social_media_trend'
  | 'people_are_searching_for'

/** Card style is chosen per SECTION by the server (`section.cardStyle`). */
export type TrendCardStyle = 'hero' | 'featured' | 'compact'

export type TrendDiscussionState = 'not_started' | 'ongoing'

/**
 * Structured trend query (wire `query`). W Trends expands this server-side
 * when resolving posts; the client may also map it onto
 * `app.bsky.feed.searchPosts` (`keywords`→q, `hashtags`→tag, `languages`→lang).
 */
export type TrendQuery = {
  keywords: string[]
  hashtags: string[]
  languages: string[]
  since?: string
}

export type TrendImage = {
  url: string
  alt: string
  // The live API sends `null` when it doesn't know the intrinsic dimensions.
  width: number | null
  height: number | null
}

export type TrendAvatar = {
  did: string
  handle: string
  avatarUrl: string
}

export type TrendCard = {
  id: string
  sourceName: string
  headline: string
  /** Nullable in the live response — some sources have no summary. */
  summary: string | null
  /** Nullable in the live response — some sources have no image. */
  image: TrendImage | null
  /** The article link. Usually a `news.google.com/rss/…` redirect. */
  url: string
  /**
   * Publisher base URL from the RSS item's `<source url="…">` (e.g.
   * `https://www.theguardian.com`). Display only — used for the favicon,
   * apple-touch-icon, and domain label so the card shows the real source
   * instead of "news.google.com". Falls back to `url` when absent.
   */
  sourceUrl?: string
  publishedAt?: string
}

/**
 * In-feed activity metric. The ranked signal/velocity data now lives on
 * `Trend.signals` / `Trend.velocity` (from the `ranking` payload); the older
 * per-trend metric fields (media mentions, searches, growth %, engagement
 * totals, …) are no longer sent or rendered and have been removed.
 * `activeVoicesCount` is optional — guard for missing values.
 */
export type TrendMetrics = {
  activeVoicesCount?: number
}

/** Strength of a single ranking source (wire `ranking.signals[].level`). */
export type TrendSignalLevel = 'high' | 'medium' | 'low'
export type TrendSignal = {
  /** 0–100 strength of this source. */
  value: number
  level: TrendSignalLevel
}

/**
 * Per-source ranking signals (wire `ranking.signals`). Each drives a badge on
 * the #1 hero — but only at `level: 'high'` (med/low render nothing).
 */
export type TrendSignals = {
  search?: TrendSignal
  social?: TrendSignal
  media?: TrendSignal
}

/**
 * Change in Google search volume since the previous ranking run (wire
 * `ranking.velocity`). `null` for brand-new trends — the velocity pill is then
 * hidden.
 */
export type TrendVelocity = {
  growthPercent: number
  /** Window the growth was measured over, e.g. 1 → "past hour". */
  sinceHours: number
}

export type Trend = {
  id: string
  title: string
  description: string
  category: TrendsCategory
  activityStatus: TrendActivityStatus
  regions: string[]
  heroImage?: TrendImage | null
  /** Structured query (wire). Optional in the mock; populated by the backend. */
  query?: TrendQuery
  metrics: TrendMetrics
  participantAvatars: TrendAvatar[]
  participantDids?: string[]
  featuredCards: TrendCard[]
  discussionState: TrendDiscussionState
  // Server-side ranking/scoping fields — not used by the UI, kept for fidelity.
  rank?: number
  score?: number
  scopes?: string[]
  lang?: string
  /**
   * Ranking explainers (wire `ranking`). Populated for the top-ranked trends;
   * `signals` drives the hero badges, `velocity` the "+N% past hour" pill.
   */
  signals?: TrendSignals
  velocity?: TrendVelocity | null
  /**
   * Social-proof fields added by the backend when `scope=foryou`. See the
   * "For you" section of `trends-how-it-works.md`. Optional everywhere else.
   */
  followedActiveCount?: number
  followedActiveAvatars?: TrendAvatar[]
}

export type TrendSection = {
  /**
   * `top` is the ranked block (cards chosen by rank, not by `cardStyle`);
   * see `TopTrendsSection`.
   */
  type: 'top' | 'highlights' | 'main' | 'category'
  title?: string
  subtitle?: string
  /** Container layout. `single` is one large card (hero). */
  layout: 'single' | 'carousel' | 'feed'
  /** Card style for every trend in this section (server-chosen). */
  cardStyle: TrendCardStyle
  trends: Trend[]
}

export type TrendsResponse = {
  sections: TrendSection[]
  cursor?: string
  generatedAt?: string
  stale?: boolean
  schemaVersion?: number
}

const MOCK_TOP_AVATARS: TrendAvatar[] = [
  {
    did: 'did:plc:top1',
    handle: 'charlie.wsocial.eu',
    avatarUrl: 'https://picsum.photos/seed/top-a/100/100',
  },
  {
    did: 'did:plc:top2',
    handle: 'ryan.wsocial.eu',
    avatarUrl: 'https://picsum.photos/seed/top-b/100/100',
  },
  {
    did: 'did:plc:top3',
    handle: 'mara.wsocial.eu',
    avatarUrl: 'https://picsum.photos/seed/top-c/100/100',
  },
  {
    did: 'did:plc:top4',
    handle: 'olen.wsocial.eu',
    avatarUrl: 'https://picsum.photos/seed/top-d/100/100',
  },
]

/**
 * Ranked "Top trends" block. The cards are chosen by rank (#1 hero, #2 featured,
 * #3–5 compact) rather than by `cardStyle`, so `TopTrendsSection` renders it
 * specially. `signals`/`velocity` populate the #1 hero's badges + velocity pill;
 * the scope label ("Trend Globally" / "Trend in {region}") is derived in the UI.
 *
 * Mock-only: real rankings arrive once the backend formats the new
 * `ranking`/`top10`/`signals`/`velocity` payload.
 */
export const MOCK_TOP_SECTION: TrendSection = {
  type: 'top',
  layout: 'feed',
  cardStyle: 'hero',
  trends: [
    {
      id: 'top-partiledardebatt',
      title: 'Partiledardebatt',
      description:
        'Discussions around immigration, energy prices and gang violence are dominating Swedish political feeds after last night’s debate.',
      category: 'politics',
      activityStatus: 'people_are_searching_for',
      regions: ['SE', 'EU', 'GLOBAL'],
      rank: 1,
      heroImage: {
        url: 'https://picsum.photos/seed/top-partiledardebatt/1200/800',
        alt: 'Party leaders on a debate stage',
        width: 1200,
        height: 800,
      },
      signals: {
        search: {value: 92, level: 'high'},
        social: {value: 78, level: 'high'},
        media: {value: 64, level: 'high'},
      },
      velocity: {growthPercent: 130, sinceHours: 1},
      metrics: {activeVoicesCount: 2847},
      participantAvatars: MOCK_TOP_AVATARS,
      featuredCards: [],
      discussionState: 'ongoing',
    },
    {
      id: 'top-gift-vid-forsta-ogonkastet',
      title: 'Gift vid första ögonkastet',
      description:
        'Reality-tv-bröllopen får sociala flöden att koka inför säsongsfinalen.',
      category: 'politics',
      activityStatus: 'social_media_trend',
      regions: ['SE', 'EU', 'GLOBAL'],
      rank: 2,
      heroImage: {
        url: 'https://picsum.photos/seed/top-gift/1200/800',
        alt: 'Man arriving at an airport',
        width: 1200,
        height: 800,
      },
      metrics: {activeVoicesCount: 2847},
      participantAvatars: MOCK_TOP_AVATARS,
      featuredCards: [],
      discussionState: 'ongoing',
    },
    {
      id: 'top-iran-us-peace-3',
      title: 'Iran US Peace',
      description:
        'Political feeds are reacting to Trump’s rallies, legal battles, and latest media appearances.',
      category: 'politics',
      activityStatus: 'news_media_trend',
      regions: ['EU', 'GLOBAL'],
      rank: 3,
      metrics: {activeVoicesCount: 2847},
      participantAvatars: MOCK_TOP_AVATARS,
      featuredCards: [],
      discussionState: 'ongoing',
    },
    {
      id: 'top-iran-us-peace-4',
      title: 'Iran US Peace',
      description:
        'Political feeds are reacting to Trump’s rallies, legal battles, and latest media appearances.',
      category: 'politics',
      activityStatus: 'news_media_trend',
      regions: ['EU', 'GLOBAL'],
      rank: 4,
      metrics: {activeVoicesCount: 2847},
      participantAvatars: MOCK_TOP_AVATARS,
      featuredCards: [],
      discussionState: 'ongoing',
    },
    {
      id: 'top-iran-us-peace-5',
      title: 'Iran US Peace',
      description:
        'Political feeds are reacting to Trump’s rallies, legal battles, and latest media appearances.',
      category: 'politics',
      activityStatus: 'news_media_trend',
      regions: ['EU', 'GLOBAL'],
      rank: 5,
      metrics: {activeVoicesCount: 2847},
      participantAvatars: MOCK_TOP_AVATARS,
      featuredCards: [],
      discussionState: 'ongoing',
    },
  ],
}

/**
 * Full mock response. Identical to `trends-mock-data.json`, with the ranked
 * `MOCK_TOP_SECTION` prepended.
 */
export const MOCK_TRENDS_RESPONSE: TrendsResponse = {
  sections: [
    MOCK_TOP_SECTION,
    {
      type: 'highlights',
      title: 'Trending topics',
      layout: 'carousel',
      cardStyle: 'compact',
      trends: [
        {
          id: 'eurovision-2026',
          title: 'Eurovision Song Contest 2026',
          description:
            'Årets schlagerfestival pågår just nu i Wien med rekordstort tittande och het diskussion online.',
          category: 'culture',
          activityStatus: 'social_media_trend',
          regions: ['SE', 'GLOBAL'],
          heroImage: {
            url: 'https://picsum.photos/seed/eurovision/1200/800',
            alt: 'Eurovision-scenen med strålkastare',
            width: 1200,
            height: 800,
          },
          metrics: {
            activeVoicesCount: 5000,
          },
          participantAvatars: [
            {
              did: 'did:plc:abc123',
              handle: 'popfan.bsky.social',
              avatarUrl: 'https://i.pravatar.cc/150?img=1',
            },
            {
              did: 'did:plc:abc124',
              handle: 'musicnerd.bsky.social',
              avatarUrl: 'https://i.pravatar.cc/150?img=2',
            },
            {
              did: 'did:plc:abc125',
              handle: 'viewer.bsky.social',
              avatarUrl: 'https://i.pravatar.cc/150?img=3',
            },
          ],
          featuredCards: [],
          discussionState: 'ongoing',
        },
        {
          id: 'world-cup-2026',
          title: 'World Cup 2026',
          description:
            'Sverige mot Brasilien i kvartsfinal ikväll – sociala medier brinner.',
          category: 'sports',
          activityStatus: 'social_media_trend',
          regions: ['SE', 'GLOBAL'],
          heroImage: {
            url: 'https://picsum.photos/seed/worldcup/1200/800',
            alt: 'Fotbollsspelare jublar',
            width: 1200,
            height: 800,
          },
          metrics: {
            activeVoicesCount: 123000,
          },
          participantAvatars: [
            {
              did: 'did:plc:s1',
              handle: 'fotbollsfan.bsky.social',
              avatarUrl: 'https://i.pravatar.cc/150?img=4',
            },
            {
              did: 'did:plc:s2',
              handle: 'sportreporter.bsky.social',
              avatarUrl: 'https://i.pravatar.cc/150?img=5',
            },
            {
              did: 'did:plc:s3',
              handle: 'supporter.bsky.social',
              avatarUrl: 'https://i.pravatar.cc/150?img=6',
            },
          ],
          featuredCards: [],
          discussionState: 'ongoing',
        },
        {
          id: 'ai-act-eu',
          title: 'EU AI Act',
          description:
            'Nya regler börjar gälla nästa månad – tech-branschen reagerar.',
          category: 'tech',
          activityStatus: 'news_media_trend',
          regions: ['SE', 'GLOBAL'],
          heroImage: {
            url: 'https://picsum.photos/seed/aiact/1200/800',
            alt: 'EU-parlamentet',
            width: 1200,
            height: 800,
          },
          metrics: {
            activeVoicesCount: 8400,
          },
          participantAvatars: [
            {
              did: 'did:plc:t1',
              handle: 'techlaw.bsky.social',
              avatarUrl: 'https://i.pravatar.cc/150?img=7',
            },
            {
              did: 'did:plc:t2',
              handle: 'policywonk.bsky.social',
              avatarUrl: 'https://i.pravatar.cc/150?img=8',
            },
          ],
          featuredCards: [],
          discussionState: 'ongoing',
        },
      ],
    },
    {
      type: 'main',
      layout: 'single',
      cardStyle: 'hero',
      trends: [
        {
          id: 'partiledardebatt',
          title: 'Partiledardebatt',
          description:
            "Discussions around immigration, energy prices and gang violence are dominating Swedish political feeds after last night's debate.",
          category: 'politics',
          activityStatus: 'news_media_trend',
          regions: ['SE'],
          heroImage: {
            url: 'https://picsum.photos/seed/partiledare/1200/800',
            alt: 'Partiledare på debattscen',
            width: 1200,
            height: 800,
          },
          metrics: {
            activeVoicesCount: 2847,
          },
          participantAvatars: [
            {
              did: 'did:plc:p1',
              handle: 'fredrik.bsky.social',
              avatarUrl: 'https://i.pravatar.cc/150?img=11',
            },
            {
              did: 'did:plc:p2',
              handle: 'johan.bsky.social',
              avatarUrl: 'https://i.pravatar.cc/150?img=12',
            },
            {
              did: 'did:plc:p3',
              handle: 'michelle.bsky.social',
              avatarUrl: 'https://i.pravatar.cc/150?img=13',
            },
            {
              did: 'did:plc:p4',
              handle: 'stina.bsky.social',
              avatarUrl: 'https://i.pravatar.cc/150?img=14',
            },
            {
              did: 'did:plc:p5',
              handle: 'karl.bsky.social',
              avatarUrl: 'https://i.pravatar.cc/150?img=15',
            },
          ],
          featuredCards: [
            {
              id: 'card-guardian-billionaires',
              sourceName: 'Aftonbladet',
              headline: 'Så gick partiledardebatten – vinnare och förlorare',
              summary:
                'Vår politiska kommentator analyserar gårdagens hetaste replikskiften.',
              image: {
                url: 'https://picsum.photos/seed/debatecard/800/600',
                alt: 'Partiledare på scen',
                width: 800,
                height: 600,
              },
              url: 'https://www.aftonbladet.se/example/partiledardebatt',
            },
          ],
          discussionState: 'ongoing',
        },
      ],
    },
    {
      type: 'main',
      layout: 'feed',
      cardStyle: 'compact',
      trends: [
        {
          id: 'banksy-new-works',
          title: 'Banksy',
          description:
            'Social media is buzzing over possible new Banksy works appearing across major cities.',
          category: 'culture',
          activityStatus: 'social_media_trend',
          regions: ['GLOBAL'],
          heroImage: null,
          metrics: {
            activeVoicesCount: 2847,
          },
          participantAvatars: [
            {
              did: 'did:plc:b1',
              handle: 'artlover.bsky.social',
              avatarUrl: 'https://i.pravatar.cc/150?img=21',
            },
            {
              did: 'did:plc:b2',
              handle: 'streetart.bsky.social',
              avatarUrl: 'https://i.pravatar.cc/150?img=22',
            },
          ],
          featuredCards: [],
          discussionState: 'ongoing',
        },
        {
          id: 'musk-vs-altman',
          title: 'Musk vs Altman',
          description:
            "People are split over OpenAI drama, AI safety claims, and who's really shaping the future of AI.",
          category: 'tech',
          activityStatus: 'news_media_trend',
          regions: ['GLOBAL'],
          heroImage: {
            url: 'https://picsum.photos/seed/musk/1200/800',
            alt: 'Elon Musk på event',
            width: 1200,
            height: 800,
          },
          metrics: {
            activeVoicesCount: 2847,
          },
          participantAvatars: [
            {
              did: 'did:plc:m1',
              handle: 'aiwatcher.bsky.social',
              avatarUrl: 'https://i.pravatar.cc/150?img=23',
            },
          ],
          featuredCards: [
            {
              id: 'card-guardian-billionaires',
              sourceName: 'The Guardian',
              headline:
                'Musk v Altman: tech bros at war over OpenAI – The Latest',
              summary:
                'Search volume is up 320% in the last 48 hours vs. last week.',
              image: {
                url: 'https://picsum.photos/seed/musk-altman/800/600',
                alt: 'Musk and Altman side by side',
                width: 800,
                height: 600,
              },
              url: 'https://www.theguardian.com/example/musk-altman',
            },
          ],
          discussionState: 'not_started',
        },
        {
          id: 'donald-trump',
          title: 'Donald Trump',
          description:
            "Political feeds are reacting to Trump's rallies, legal battles, and latest media appearances.",
          category: 'politics',
          activityStatus: 'news_media_trend',
          regions: ['GLOBAL'],
          heroImage: null,
          metrics: {
            activeVoicesCount: 2847,
          },
          participantAvatars: [
            {
              did: 'did:plc:dt1',
              handle: 'uspol.bsky.social',
              avatarUrl: 'https://i.pravatar.cc/150?img=24',
            },
          ],
          featuredCards: [],
          discussionState: 'ongoing',
        },
      ],
    },
    {
      type: 'main',
      layout: 'feed',
      cardStyle: 'featured',
      trends: [
        {
          id: 'gift-vid-forsta-ogonkastet',
          title: 'Gift vid första ögonkastet',
          description:
            'Tittarsiffrorna rasade efter gårdagens avsnitt – stora reaktioner i sociala flöden.',
          category: 'culture',
          activityStatus: 'social_media_trend',
          regions: ['SE'],
          heroImage: null,
          metrics: {
            activeVoicesCount: 8946,
          },
          participantAvatars: [
            {
              did: 'did:plc:gv1',
              handle: 'ryanfoster.wsocial.eu',
              avatarUrl: 'https://i.pravatar.cc/150?img=25',
            },
            {
              did: 'did:plc:gv2',
              handle: 'tvfan.bsky.social',
              avatarUrl: 'https://i.pravatar.cc/150?img=26',
            },
            {
              did: 'did:plc:gv3',
              handle: 'realityjunkie.bsky.social',
              avatarUrl: 'https://i.pravatar.cc/150?img=27',
            },
          ],
          featuredCards: [],
          discussionState: 'ongoing',
        },
      ],
    },
    {
      type: 'main',
      layout: 'feed',
      cardStyle: 'compact',
      trends: [
        {
          id: 'kriget-i-ukraina',
          title: 'Kriget i Ukraina',
          description:
            'Discussions about western support, war fatigue and the risk of escalation continue dominating political conversations.',
          category: 'politics',
          activityStatus: 'news_media_trend',
          regions: ['SE', 'GLOBAL'],
          heroImage: null,
          metrics: {
            activeVoicesCount: 2847,
          },
          participantAvatars: [
            {
              did: 'did:plc:uk1',
              handle: 'newswatcher.bsky.social',
              avatarUrl: 'https://i.pravatar.cc/150?img=28',
            },
          ],
          featuredCards: [],
          discussionState: 'ongoing',
        },
      ],
    },
    {
      type: 'main',
      layout: 'feed',
      cardStyle: 'featured',
      trends: [
        {
          id: 'trump-tariffs',
          title: 'Trump tariffs',
          description:
            'New tariff policies are creating waves across European business communities.',
          category: 'politics',
          activityStatus: 'people_are_searching_for',
          regions: ['GLOBAL'],
          heroImage: null,
          metrics: {
            activeVoicesCount: 4200,
          },
          participantAvatars: [
            {
              did: 'did:plc:tt1',
              handle: 'ryanfoster.wsocial.eu',
              avatarUrl: 'https://i.pravatar.cc/150?img=29',
            },
          ],
          featuredCards: [],
          discussionState: 'ongoing',
        },
      ],
    },
    {
      type: 'main',
      layout: 'feed',
      cardStyle: 'compact',
      trends: [
        {
          id: 'nato-summit',
          title: 'Nato',
          description:
            'Users are debating military escalation, European security and whether NATO unity is starting to weaken.',
          category: 'politics',
          activityStatus: 'news_media_trend',
          regions: ['SE', 'GLOBAL'],
          heroImage: null,
          metrics: {
            activeVoicesCount: 2847,
          },
          participantAvatars: [],
          featuredCards: [],
          discussionState: 'ongoing',
        },
        {
          id: 'benjamin-netanyahu',
          title: 'Benjamin Netanyahu',
          description:
            'Online discussions are increasingly focused on Gaza, international pressure and long-term regional stability.',
          category: 'politics',
          activityStatus: 'news_media_trend',
          regions: ['GLOBAL'],
          heroImage: null,
          metrics: {
            activeVoicesCount: 2847,
          },
          participantAvatars: [],
          featuredCards: [],
          discussionState: 'ongoing',
        },
        {
          id: 'knesset',
          title: 'Knesset',
          description:
            'Political communities are arguing over internal divisions in Israel and how the war is reshaping public opinion.',
          category: 'politics',
          activityStatus: 'news_media_trend',
          regions: ['GLOBAL'],
          heroImage: null,
          metrics: {
            activeVoicesCount: 2847,
          },
          participantAvatars: [],
          featuredCards: [],
          discussionState: 'ongoing',
        },
      ],
    },
    {
      type: 'main',
      layout: 'single',
      cardStyle: 'hero',
      trends: [
        {
          id: 'premier-league',
          title: 'Premier League',
          description:
            "Arsenal's Premier League win is dominating online discussion, from fan celebrations to rival reactions.",
          category: 'sports',
          activityStatus: 'social_media_trend',
          regions: ['GLOBAL'],
          heroImage: {
            url: 'https://picsum.photos/seed/arsenal/1200/800',
            alt: 'Arsenalspelare firar',
            width: 1200,
            height: 800,
          },
          metrics: {
            activeVoicesCount: 2847,
          },
          participantAvatars: [
            {
              did: 'did:plc:pl1',
              handle: 'gunner.bsky.social',
              avatarUrl: 'https://i.pravatar.cc/150?img=30',
            },
            {
              did: 'did:plc:pl2',
              handle: 'fotbollsfan.bsky.social',
              avatarUrl: 'https://i.pravatar.cc/150?img=31',
            },
          ],
          featuredCards: [
            {
              id: 'card-bbc-arsenal',
              sourceName: 'BBC Sport',
              headline: 'Arsenal seal Premier League title in dramatic fashion',
              summary:
                'Late winner against Spurs decides the title race after five months at the top.',
              image: {
                url: 'https://picsum.photos/seed/arsenal-bbc/800/450',
                alt: 'Arsenal players celebrating',
                width: 800,
                height: 450,
              },
              url: 'https://www.bbc.co.uk/sport/example/arsenal-title',
            },
            {
              id: 'card-guardian-arsenal',
              sourceName: 'The Guardian',
              headline: 'How Arsenal finally ended their two-decade title wait',
              summary:
                'Tactical analysis of the moves that brought silverware back to North London.',
              image: {
                url: 'https://picsum.photos/seed/arsenal-guardian/800/450',
                alt: 'Match analysis graphic',
                width: 800,
                height: 450,
              },
              url: 'https://www.theguardian.com/football/example/arsenal-analysis',
            },
          ],
          discussionState: 'ongoing',
        },
      ],
    },
    // {
    //   type: 'category',
    //   title: 'Trending within sport',
    //   layout: 'carousel',
    //   cardStyle: 'compact',
    //   trends: [
    //     {
    //       id: 'mourinho',
    //       title: 'Mourinho',
    //       description:
    //         'Tränarens senaste presskonferens skapar debatt bland fotbollsfans.',
    //       category: 'sports',
    //       activityStatus: 'social_media_trend',
    //       regions: ['GLOBAL'],
    //       heroImage: {
    //         url: 'https://picsum.photos/seed/mourinho/600/600',
    //         alt: 'José Mourinho',
    //         width: 600,
    //         height: 600,
    //       },
    //       metrics: {
    //         activeVoicesCount: 5000,
    //         postsPerHour: 64,
    //         growthPercent: 41,
    //         totalReplies: 3200,
    //         totalReposts: 2100,
    //         totalLikes: 7400,
    //       },
    //       participantAvatars: [
    //         {
    //           did: 'did:plc:mo1',
    //           handle: 'fotbollsexpert.bsky.social',
    //           avatarUrl: 'https://i.pravatar.cc/150?img=32',
    //         },
    //       ],
    //       featuredCards: [],
    //       discussionState: 'ongoing',
    //     },
    //     {
    //       id: 'aaron-rai',
    //       title: 'Aaron Rai',
    //       description:
    //         'Engelsmannen leder PGA-touren – stora reaktioner från golfvärlden.',
    //       category: 'sports',
    //       activityStatus: 'social_media_trend',
    //       regions: ['GLOBAL'],
    //       heroImage: {
    //         url: 'https://picsum.photos/seed/aaronrai/600/600',
    //         alt: 'Aaron Rai på golfbanan',
    //         width: 600,
    //         height: 600,
    //       },
    //       metrics: {
    //         activeVoicesCount: 123000,
    //         postsPerHour: 84,
    //         growthPercent: 178,
    //         totalReplies: 4100,
    //         totalReposts: 3800,
    //         totalLikes: 12000,
    //       },
    //       participantAvatars: [
    //         {
    //           did: 'did:plc:ar1',
    //           handle: 'golffan.bsky.social',
    //           avatarUrl: 'https://i.pravatar.cc/150?img=33',
    //         },
    //       ],
    //       featuredCards: [],
    //       discussionState: 'ongoing',
    //     },
    //   ],
    // },
  ],
}

/**
 * Mock replacement for `GET /trends?scope=…&region=…&category=…`.
 *
 * - `scope` is mostly mock-ignored, except that `scope: 'region'` activates
 *   region filtering (below). Once the backend lands, scope determines which
 *   sections come back.
 * - `region`, when scope is `region`, filters every section's trends to those
 *   whose `regions` array includes the selected country code (global trends —
 *   tagged `GLOBAL` — are always kept so a region is never empty).
 * - `category`, when set, filters every section's trends to that category.
 *
 * Both filters drop sections that end up empty.
 */
export function getMockTrends({
  scope,
  region,
  category,
}: {
  scope: TrendsScope
  region?: string | null
  category: TrendsCategory | null
}): TrendsResponse {
  const filterRegion = scope === 'region' && !!region
  if (!category && !filterRegion) return MOCK_TRENDS_RESPONSE
  const sections = MOCK_TRENDS_RESPONSE.sections
    .map(section => ({
      ...section,
      trends: section.trends.filter(t => {
        if (category && t.category !== category) return false
        if (
          filterRegion &&
          // 'EUROPE' is the whole-continent pseudo-region — every mock trend
          // is European or global, so it matches everything. The backend
          // resolves it properly server-side.
          region !== 'EUROPE' &&
          !t.regions.includes(region) &&
          !t.regions.includes('GLOBAL')
        ) {
          return false
        }
        return true
      }),
    }))
    .filter(section => section.trends.length > 0)
  return {...MOCK_TRENDS_RESPONSE, sections}
}

/**
 * Client-side category filter. We fetch trends by region only and filter by
 * category here, so switching chips never refetches. Keeps each section but
 * drops trends outside `category`, then drops any section left empty. Passing
 * `null` (the "All" chip) returns the response unchanged.
 *
 * Selecting a category flattens EVERY trend in that category — across all
 * sections, including the carousels that are hidden on the "All" view and the
 * ranked `top` block — into a single vertical feed, de-duplicated by id (a trend
 * that appears in both a carousel and the main feed shows once). This keeps the
 * chips and results in sync: any chip that's shown is guaranteed to have
 * content, and there's no standalone carousel/top block on a category view.
 */
export function filterTrendsResponseByCategory(
  response: TrendsResponse,
  category: TrendsCategory | null,
): TrendsResponse {
  if (!category) return response
  const seen = new Set<string>()
  const trends: Trend[] = []
  for (const section of response.sections) {
    for (const trend of section.trends) {
      if (trend.category === category && !seen.has(trend.id)) {
        seen.add(trend.id)
        trends.push(trend)
      }
    }
  }
  const sections: TrendSection[] =
    trends.length > 0
      ? [{type: 'category', layout: 'feed', cardStyle: 'compact', trends}]
      : []
  return {...response, sections}
}

/**
 * Sections we hide for now regardless of view. Currently: every carousel
 * section (the backend's category carousels + "Trending topics"). The
 * synthesized "Your interests" block is also a carousel but is built
 * separately, so it's never passed through this check.
 *
 * Temporary — remove this filter to bring carousels back.
 */
function isHiddenForNow(section: TrendSection): boolean {
  return section.layout === 'carousel'
}

/**
 * Replace the backend's "Trending topics" highlights block with a personalised
 * "Your interests" block under the "All" chip.
 *
 * Section order under "All" is: the ranked `top` block (kept first), then the
 * "Your interests" block, then the remaining category sections. The original
 * `highlights` section is always dropped.
 *
 * "Your interests" gathers every trend from the category sections whose
 * `category` is one of the viewer's interests, de-duplicated by id, with rules
 * that avoid showing the same trend twice:
 *   1. Trends already in the ranked `top` block are excluded from "Your
 *      interests" AND removed from the category sections below — so a top trend
 *      (e.g. the #1 hero) never appears a second time.
 *   2. A trend surfaced into "Your interests" is REMOVED from its category
 *      section below (moved, not copied). Category sections left empty are
 *      dropped.
 *
 * Because `category` is an interest slug (see `TrendsCategory`), matching is a
 * plain set-membership test. With no interests (or none matching) no block is
 * added; the feed is just `top` + the category sections unchanged.
 *
 * `title` is passed in already localised (the data layer has no i18n context).
 */
/** Max trends in the "Your interests" block. */
export const YOUR_INTERESTS_MAX = 8
/** Max trends shown across all sections below "Your interests" combined. */
export const REMAINING_TRENDS_MAX = 30

export function withInterestsHighlights(
  response: TrendsResponse,
  interests: readonly string[],
  title: string,
): TrendsResponse {
  const interestSet = new Set(interests)
  // The ranked block stays pinned to the top, ahead of "Your interests".
  const top = response.sections.filter(s => s.type === 'top')
  const topIds = new Set<string>()
  for (const section of top) {
    for (const trend of section.trends) topIds.add(trend.id)
  }
  // Category sections feed the "Your interests" aggregation and follow it.
  const categories = response.sections.filter(
    s => s.type !== 'top' && s.type !== 'highlights',
  )

  // Pass 1: candidate trends — interest-matched, not already in the top block,
  // de-duplicated by id (first-appearance order). Trends with an image are
  // surfaced ahead of image-less ones (stable, so order is otherwise kept):
  // the block is a carousel of image cards, and a row of placeholders reads as
  // broken when illustrated trends were available further down. "Your
  // interests" is capped at YOUR_INTERESTS_MAX; only the trends that actually
  // land in the block are removed from their place below (any extras stay
  // where they are).
  const seenIds = new Set<string>()
  const candidates: Trend[] = []
  for (const section of categories) {
    for (const trend of section.trends) {
      if (
        interestSet.has(trend.category) &&
        !topIds.has(trend.id) &&
        !seenIds.has(trend.id)
      ) {
        seenIds.add(trend.id)
        candidates.push(trend)
      }
    }
  }
  const interestTrends = [
    ...candidates.filter(trend => trend.heroImage),
    ...candidates.filter(trend => !trend.heroImage),
  ].slice(0, YOUR_INTERESTS_MAX)
  const surfacedIds = new Set(interestTrends.map(trend => trend.id))

  // Pass 2: rebuild the sections below "Your interests". Carousel sections are
  // hidden for now (their interest trends were already surfaced above). A trend
  // is removed from the remaining feed sections if it's already shown above —
  // either surfaced into "Your interests" or part of the ranked top block (so
  // the #1 hero never appears twice). The total is capped at
  // REMAINING_TRENDS_MAX (truncating in order); empty sections are dropped.
  const remainingCategories: TrendSection[] = []
  let budget = REMAINING_TRENDS_MAX
  for (const section of categories) {
    if (isHiddenForNow(section)) continue
    if (budget <= 0) break
    const kept = section.trends
      .filter(trend => !surfacedIds.has(trend.id) && !topIds.has(trend.id))
      .slice(0, budget)
    if (kept.length > 0) {
      remainingCategories.push({...section, trends: kept})
      budget -= kept.length
    }
  }

  const interestsSection: TrendSection | null =
    interestTrends.length > 0
      ? {
          type: 'highlights',
          title,
          layout: 'carousel',
          cardStyle: 'compact',
          trends: interestTrends,
        }
      : null

  return {
    ...response,
    sections: [
      ...top,
      ...(interestsSection ? [interestsSection] : []),
      ...remainingCategories,
    ],
  }
}

/** Signal display order — search, then social, then media (matches Figma). */
export const TREND_SIGNAL_ORDER = ['search', 'social', 'media'] as const
export type TrendSignalKey = (typeof TREND_SIGNAL_ORDER)[number]

/**
 * Signal keys that earn a badge on the #1 hero — only those at `level: 'high'`,
 * returned in display order. Med/low (and missing signals) render nothing.
 */
export function highSignalKeys(signals?: TrendSignals): TrendSignalKey[] {
  if (!signals) return []
  return TREND_SIGNAL_ORDER.filter(key => signals[key]?.level === 'high')
}

/**
 * Trend match for a free-form search query.
 *
 * Used by `/search` to decide whether to render the `TrendSearchHeader` +
 * `FeaturedCardsCarousel` enrichment above the Bluesky-native search
 * results. Matching is intentionally strict — case-insensitive equality on
 * either `trend.title` or `trend.id` (slug) — so "explore trend" CTAs (which
 * navigate with `q = trend.title`) always match, while ad-hoc queries only
 * match when the user clearly typed the trend's name.
 *
 * Returns the first match across all sections; duplicates between sections
 * resolve to whichever section the backend listed first.
 */
export function findMatchingTrend(
  query: string,
  response: TrendsResponse,
): Trend | null {
  const normalized = query.trim().toLowerCase()
  if (!normalized) return null
  for (const section of response.sections) {
    for (const trend of section.trends) {
      if (trend.title.toLowerCase() === normalized) return trend
      if (trend.id.toLowerCase() === normalized) return trend
    }
  }
  return null
}
